import { FC, forwardRef, useEffect, useRef, type ReactNode } from "react";
import { PrinterOutlined } from "@ant-design/icons";
import { Button, Col, Descriptions, Modal, Row, Spin, Typography } from "antd";
// import { Table } from "antd";
// import type { ColumnsType } from "antd/es/table";
import styled from "@emotion/styled";
import TransportLogo from "components/ManageProduct/CreateProduct/ProductSKUCreate/TransportLogo";
import { formatWithCommas } from "components/ManageProduct/CreateProduct/ProductSKUCreate/helpers";
import { normalizeSpeedLogo } from "../ParcelShared/speedCarrierUtils";
import { parcelIconActionButtonStyle } from "../manageParcelLayout";
// import type { ParcelLineItem } from "./parcelCreateTypes";
// import { calcLineTotalPrice } from "./parcelCreateTypes";

export type ParcelCreateConfirmSummary = {
  /** ชื่อพัสดุที่ส่ง (รายการเดียว) */
  parcelName: string;
  // products: ParcelLineItem[]; // เคยแสดงได้มากกว่า 1 รายการ (ตาราง)
  packaging: {
    typeLabel?: string;
    width?: number;
    length?: number;
    height?: number;
    weightGrams?: number;
  };
  sender?: {
    addressLine: string;
    subDistrict?: string;
    district?: string;
    province?: string;
    postcode?: string;
    phone: string;
  };
  destination: {
    fullname: string;
    addressLine: string;
    subDistrict?: string;
    district?: string;
    province?: string;
    postcode?: string;
    telephone1: string;
    telephone2?: string;
  };
  carrier: {
    name: string;
    code: string;
    logo?: string;
    shippingPrice?: number;
    /** ราคาค่าขนส่งดิบ × multiplier — ให้ตรงกับโควต้าที่โรงงานเห็นจริง (ดู ParcelCreditCard) */
    creditUsed?: number;
  };
  referenceNo?: string;
  remark?: string;
  codAmount?: number;
};

type ParcelCreateConfirmModalProps = {
  open: boolean;
  summary: ParcelCreateConfirmSummary | null;
  confirming?: boolean;
  mode?: "confirm" | "result";
  result?: {
    statusText: string;
    trackingNumber?: string;
    createdAtText?: string;
    canPrint?: boolean;
  } | null;
  onClose: () => void;
  onConfirm: () => void;
  onPrint?: () => void;
};

/** ความกว้างหัวข้อส่วนและ label ใน Descriptions ให้เท่ากันทั้ง Modal */
const CONFIRM_LABEL_WIDTH = 200;

const confirmDescProps = {
  bordered: true as const,
  size: "small" as const,
  column: 1 as const,
  labelStyle: { width: CONFIRM_LABEL_WIDTH, fontWeight: 500 },
};

const ConfirmSection = styled.section`
  margin-bottom: 16px;
`;

const SectionHeading = styled(Typography.Title)`
  && {
    margin-top: 0;
    margin-bottom: 8px;
    width: ${CONFIRM_LABEL_WIDTH}px;
    max-width: 100%;
    font-size: 14px;
    font-weight: 600;
  }
`;

type ConfirmSectionBlockProps = {
  title: string;
  children: ReactNode;
};

const ConfirmSectionBlock = forwardRef<HTMLElement, ConfirmSectionBlockProps>(
  ({ title, children }, ref) => (
    <ConfirmSection ref={ref}>
      <SectionHeading level={5}>{title}</SectionHeading>
      {children}
    </ConfirmSection>
  )
);
ConfirmSectionBlock.displayName = "ConfirmSectionBlock";

const formatBaht = (value?: number) =>
  value != null ? `${formatWithCommas(Math.ceil(value))} บาท` : "-";

/** ตำบล / อำเภอ / จังหวัด / รหัสไปรษณีย์ — บรรทัดเดียวพร้อมคำนำหน้า */
const formatAdminAreaLine = (params: {
  subDistrict?: string;
  district?: string;
  province?: string;
  postcode?: string;
}) => {
  const parts = [
    params.subDistrict?.trim() ? `ตำบล ${params.subDistrict.trim()}` : null,
    params.district?.trim() ? `อำเภอ ${params.district.trim()}` : null,
    params.province?.trim() ? `จังหวัด ${params.province.trim()}` : null,
    params.postcode?.trim() ? `รหัสไปรษณีย์ ${params.postcode.trim()}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "-";
};

// const productColumns: ColumnsType<ParcelLineItem> = [
//   { title: "รหัส", dataIndex: "code", width: 88, render: (v) => v || "-" },
//   { title: "ชื่อสินค้า", dataIndex: "name", ellipsis: true },
//   { title: "จำนวน", dataIndex: "qty", width: 64, align: "right" },
//   {
//     title: "ราคาต่อหน่วย",
//     dataIndex: "pricePerUnit",
//     width: 100,
//     align: "right",
//     render: (v) => (v != null ? formatBaht(Number(v)) : "-"),
//   },
//   {
//     title: "ราคารวม",
//     key: "total",
//     width: 90,
//     align: "right",
//     render: (_, row) => formatBaht(calcLineTotalPrice(row.qty, row.pricePerUnit)),
//   },
// ];

const ParcelCreateConfirmModal: FC<ParcelCreateConfirmModalProps> = ({
  open,
  summary,
  confirming,
  mode = "confirm",
  result,
  onClose,
  onConfirm,
  onPrint,
}) => {
  const dest = summary?.destination;
  const sender = summary?.sender;
  const isResultMode = mode === "result";
  const resultSectionRef = useRef<HTMLElement>(null);

  /** หลังกดยืนยันทำรายการ — เลื่อนไปส่วนผลด้านล่างให้เห็นสถานะกำลังดำเนินการ */
  useEffect(() => {
    if (!open || !isResultMode) return;
    const timer = window.setTimeout(() => {
      resultSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [open, isResultMode, confirming, result?.statusText, result?.trackingNumber]);

  return (
    <Modal
      open={open}
      title="สรุปรายละเอียดก่อนสร้างรายการส่งพัสดุ"
      width={720}
      destroyOnClose
      maskClosable={!confirming}
      closable={!confirming}
      onCancel={onClose}
      footer={
        isResultMode
          ? [
              <Button key="close" type="primary" onClick={onClose} disabled={confirming}>
                ปิด
              </Button>,
            ]
          : [
              <Button key="edit" onClick={onClose} disabled={confirming}>
                แก้ไข
              </Button>,
              <Button key="confirm" type="primary" loading={confirming} onClick={onConfirm}>
                ยืนยันทำรายการ
              </Button>,
            ]
      }
    >
      {summary ? (
        <>
          <ConfirmSectionBlock title="1. ที่อยู่ผู้รับ">
            <Descriptions {...confirmDescProps}>
              <Descriptions.Item label="ชื่อผู้รับ">{dest?.fullname}</Descriptions.Item>
              <Descriptions.Item label="ที่อยู่">{dest?.addressLine}</Descriptions.Item>
              <Descriptions.Item label="">
                {formatAdminAreaLine({
                  subDistrict: dest?.subDistrict,
                  district: dest?.district,
                  province: dest?.province,
                  postcode: dest?.postcode,
                })}
              </Descriptions.Item>
              <Descriptions.Item label="เบอร์โทร">{dest?.telephone1}</Descriptions.Item>
              {dest?.telephone2 ? (
                <Descriptions.Item label="เบอร์โทร 2">{dest.telephone2}</Descriptions.Item>
              ) : null}
            </Descriptions>
          </ConfirmSectionBlock>

          <ConfirmSectionBlock title="2. ที่อยู่ผู้ส่ง (จุดรับสินค้า)">
            {sender ? (
              <Descriptions {...confirmDescProps}>
                <Descriptions.Item label="ที่อยู่">{sender.addressLine}</Descriptions.Item>
                <Descriptions.Item label="">
                  {formatAdminAreaLine({
                    subDistrict: sender.subDistrict,
                    district: sender.district,
                    province: sender.province,
                    postcode: sender.postcode,
                  })}
                </Descriptions.Item>
                <Descriptions.Item label="เบอร์โทร">{sender.phone}</Descriptions.Item>
              </Descriptions>
            ) : (
              <Typography.Text type="secondary">
                ยังไม่มีข้อมูลจุดรับสินค้า — ตรวจสอบการตั้งค่า Speed merchant
              </Typography.Text>
            )}
          </ConfirmSectionBlock>

          <ConfirmSectionBlock title="3. พัสดุที่ส่งและบรรจุภัณฑ์">
            <Descriptions {...confirmDescProps} column={2}>
              <Descriptions.Item label="พัสดุที่ต้องการส่ง" span={2}>
                {summary.parcelName?.trim() || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="ประเภทบรรจุภัณฑ์" span={2}>
                {summary.packaging.typeLabel || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="กว้าง (ซม.)">
                {summary.packaging.width ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="ยาว (ซม.)">
                {summary.packaging.length ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="สูง (ซม.)">
                {summary.packaging.height ?? "-"}
              </Descriptions.Item>
              <Descriptions.Item label="น้ำหนัก (กรัม)">
                {summary.packaging.weightGrams ?? "-"}
              </Descriptions.Item>
            </Descriptions>
            {/* เคยแสดงได้มากกว่า 1 รายการ (ตารางสินค้า)
            <Table ... />
            */}
          </ConfirmSectionBlock>

          <ConfirmSectionBlock title="4. ขนส่ง">
            <Descriptions {...confirmDescProps}>
              <Descriptions.Item label="บริษัทขนส่ง">
                <Row align="middle" gutter={12} wrap={false}>
                  <Col flex="none">
                    <TransportLogo
                      src={normalizeSpeedLogo(summary.carrier.logo)}
                      alt={summary.carrier.name}
                      height={40}
                      width={64}
                    />
                  </Col>
                  <Col flex="auto">
                    <Typography.Text strong>{summary.carrier.name}</Typography.Text>
                  </Col>
                </Row>
              </Descriptions.Item>
              <Descriptions.Item label="ราคาค่าขนส่ง">
                <Typography.Text strong>
                  {formatBaht(summary.carrier.shippingPrice)}
                </Typography.Text>
              </Descriptions.Item>
              {summary.carrier.creditUsed != null && (
                <Descriptions.Item label="เครดิตที่ใช้">
                  <Typography.Text strong style={{ color: "#1890ff" }}>
                    {formatWithCommas(Math.ceil(summary.carrier.creditUsed))} เครดิต
                  </Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </ConfirmSectionBlock>

          {isResultMode ? (
            <ConfirmSectionBlock title="5. ผลการทำรายการ" ref={resultSectionRef}>
              <Descriptions {...confirmDescProps}>
                <Descriptions.Item label="สถานะการดำเนินการ">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    {result?.statusText || "กำลังเชื่อมต่อกับบริษัทขนส่งโปรดรอสักครู่"}
                    {confirming ? <Spin size="small" /> : null}
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="เลขพัสดุ">
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography.Text strong>
                      {result?.trackingNumber?.trim() || "-"}
                    </Typography.Text>
                    <Button
                      type="default"
                      size="small"
                      icon={<PrinterOutlined />}
                      onClick={onPrint}
                      disabled={!result?.canPrint || confirming}
                      title="พิมพ์ Label"
                      aria-label="พิมพ์ Label"
                      style={parcelIconActionButtonStyle}
                    />
                  </span>
                </Descriptions.Item>
                <Descriptions.Item label="วันเวลาที่ทำรายการ">
                  {result?.createdAtText?.trim() || "-"}
                </Descriptions.Item>
              </Descriptions>
            </ConfirmSectionBlock>
          ) : null}

          {/* ขั้นตอน 5 อื่นๆ — ปิดชั่วคราว (เปิดพร้อม ParcelOthersSection ในฟอร์ม)
          <ConfirmSectionBlock title="6. อื่น ๆ">
            <Descriptions {...confirmDescProps}>
              <Descriptions.Item label="เลขอ้างอิง">
                {summary.referenceNo?.trim() || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="หมายเหตุ">
                {summary.remark?.trim() || "-"}
              </Descriptions.Item>
              {(summary.codAmount ?? 0) > 0 ? (
                <Descriptions.Item label="COD">
                  {formatBaht(summary.codAmount)}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </ConfirmSectionBlock>
          */}
        </>
      ) : null}
    </Modal>
  );
};

export default ParcelCreateConfirmModal;
